/**
 * PharmaIntel API 客户端
 * 用于前端与 FastAPI 后端通信
 */

function getDefaultApiBase(): string {
  if (typeof window === "undefined") {
    return "http://localhost:18765";
  }

  const { hostname, protocol } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1") {
    return "http://localhost:18765";
  }

  if (hostname.endsWith(".pages.dev")) {
    return "/api";
  }

  return `${protocol}//${hostname}:18765`;
}

const DEFAULT_API_BASE = getDefaultApiBase();

export const API_BASE = import.meta.env.VITE_API_URL || DEFAULT_API_BASE;

/** 数据源状态类型 */
export type DataSourceStatusType = "pending" | "running" | "success" | "error" | "not_found";

/** 错误信息分类 */
export type ErrorType = "timeout" | "connection_error" | "rate_limited" | "server_error" | "client_error" | "parse_error" | "unknown";

/** 单个数据源的实时进度信息 */
export interface DataSourceProgress {
  /** 数据源标识，如 "fda", "cde_reflisted" */
  id: string;
  /** 数据源显示名称，如 "FDA (openFDA)", "CDE 参比制剂目录" */
  name: string;
  /** 当前状态 */
  status: DataSourceStatusType;
  /** 状态描述消息，如 "查询中", "完成 (12.5s)" */
  message: string;
  /** 开始时间戳（毫秒） */
  started_at?: number;
  /** 完成时间戳（毫秒） */
  completed_at?: number;
  /** 已耗时（秒） */
  elapsed?: number;
  /** 错误详情，仅 status === "error" 时存在 */
  error_info?: {
    error_type: ErrorType;
    error_code: string | number;
    error_message: string;
    retry_after?: number;
  };
}

/** 进度统计摘要 */
export interface ProgressSummary {
  /** 总数据源数 */
  total: number;
  pending: number;
  running: number;
  success: number;
  error: number;
  not_found: number;
  /** 完成百分比 0-100 */
  percentage: number;
  /** 预估剩余时间（秒） */
  estimated_remaining: number;
}

export interface TaskStatus {
  task_id: string;
  status: "PENDING" | "RUNNING" | "DONE" | "ERROR" | "CANCELLED";
  message: string;
  drug_name: string;
  done: boolean;
  result?: QueryResult;

  /** 各数据源的实时进度状态 */
  sources_progress?: DataSourceProgress[];
  /** 进度统计摘要 */
  progress_summary?: ProgressSummary;
}

/** 数据源配置信息（从 /api/datasources 获取） */
export interface DatasourceConfig {
  id: string;
  name: string;
  name_en: string;
  icon: string;
  enabled: boolean;
  timeout: number;
  order: number;
  estimated_duration: number;
}

/** /api/datasources 接口响应格式 */
export interface DatasourcesResponse {
  datasources: DatasourceConfig[];
  total: number;
  enabled_count: number;
}

/** DataSourceProgress 组件使用的 props 类型 */
export interface DataSourceStatus {
  id: string;
  name: string;
  icon?: string;
  status: DataSourceStatusType;
  message?: string;
  elapsed?: number;
}

export interface QueryResult {
  task_id: string;
  drug_name: string;
  status: string;
  message: string;
  llm_status: string;
  report_path: string;
  llm_report_path: string;
  report_text: string;
  completed_at: number;
  data: {
    fda?: Record<string, unknown>;
    fda_guid?: Record<string, unknown>;
    ema?: Record<string, unknown>;
    pmda?: Record<string, unknown>;
    pubmed?: Record<string, unknown>;
    pubchem?: Record<string, unknown>;
    ich_m13a?: Record<string, unknown>;
    cde_reflisted?: Record<string, unknown>;
    cde_trials?: Record<string, unknown>;
    nmpa?: Record<string, unknown>;
    fda_review?: Record<string, unknown>;
    cde_yzxpj?: Record<string, unknown>;
    dailymed?: Record<string, unknown>;
  };

  /** 已返回的数据源数量 */
  datasource_count?: number;
  /** 总耗时（秒） */
  total_time?: number;
}

export interface Source {
  id: string;
  name: string;
  enabled: boolean;
  description: string;
  icon: string;
}

export interface SourceHealth {
  id: string;
  name: string;
  status: "healthy" | "error" | "not_configured";
  response_time?: number;
  message: string;
}

export interface SystemStats {
  tasks: {
    total: number;
    pending: number;
    running: number;
    completed: number;
  };
  storage: {
    output_dir: string;
    output_size_bytes: number;
    output_size_mb: number;
  };
  recent_drugs: Array<{
    task_id: string;
    drug_name: string;
    status: string;
    created_at: number;
  }>;
  server_time: number;
}

export interface SystemConfig {
  llm_provider?: string;
  llm_configured: boolean;
  port: number;
  host: string;
  features: {
    enable_cde: boolean;
    enable_pubmed: boolean;
    enable_pubchem: boolean;
    enable_ich_m13a: boolean;
    enable_nmpa: boolean;
  };
}

// 基础查询 API
export async function submitQuery(
  drugName: string,
  selectedDatasources?: string[],
  drugNameCn?: string,
  dosageForm?: string,
  specification?: string,
): Promise<{ task_id: string }> {
  const body: Record<string, unknown> = { drug_name: drugName, sources: {} };
  if (drugNameCn) {
    body.drug_name_cn = drugNameCn;
  }
  if (dosageForm) {
    body.dosage_form = dosageForm;
  }
  if (specification) {
    body.specification = specification;
  }
  if (selectedDatasources && selectedDatasources.length > 0) {
    body.selected_datasources = selectedDatasources;
  }
  const res = await fetch(`${API_BASE}/query`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`提交失败: ${res.status}`);
  return res.json();
}

export async function pollTask(taskId: string): Promise<TaskStatus> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 15000);
  try {
    const res = await fetch(`${API_BASE}/tasks/${taskId}`, {
      signal: controller.signal,
      cache: "no-store",
    });
    if (!res.ok) throw new Error(`查询失败: ${res.status}`);
    return res.json();
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new Error("查询状态请求超时，正在重新连接");
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }
}

export async function downloadReport(taskId: string): Promise<Blob> {
  const res = await fetch(`${API_BASE}/tasks/${taskId}/report`);
  if (!res.ok) throw new Error(`下载失败: ${res.status}`);
  return res.blob();
}

export async function healthCheck(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/health`);
    return res.ok;
  } catch {
    return false;
  }
}

// 管理 API
export async function getSources(): Promise<{ sources: Source[] }> {
  const res = await fetch(`${API_BASE}/api/sources`);
  if (!res.ok) throw new Error(`获取数据源失败: ${res.status}`);
  return res.json();
}

export async function fetchDatasources(retries: number = 2): Promise<DatasourcesResponse> {
  const delays = [1000, 2000];
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(`${API_BASE}/api/datasources`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(
          body?.detail?.message || `获取数据源配置失败: ${res.status}`
        );
      }
      return await res.json();
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));
      if (attempt < retries) {
        const delay = delays[attempt] || 2000;
        console.warn(`[api] fetchDatasources 第${attempt + 1}次失败，${delay}ms后重试...`, lastError.message);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError || new Error("获取数据源配置失败：已达到最大重试次数");
}

/** 硬编码的降级数据源列表（当 API 持续不可用时使用） */
export const FALLBACK_DATASOURCES: DatasourceConfig[] = [
  { id: "fda", name: "FDA 药品信息", name_en: "FDA Drugs", icon: "🇺🇸", enabled: true, timeout: 30, order: 1, estimated_duration: 30 },
  { id: "fda_guid", name: "FDA BE 指南(PSG)", name_en: "FDA PSG", icon: "📋", enabled: true, timeout: 90, order: 2, estimated_duration: 90 },
  { id: "ema", name: "EMA 欧洲药品管理局", name_en: "EMA", icon: "🇪🇺", enabled: true, timeout: 120, order: 3, estimated_duration: 120 },
  { id: "ich_m13a", name: "ICH M13A 低风险豁免", name_en: "ICH M13A", icon: "🏆", enabled: true, timeout: 20, order: 4, estimated_duration: 20 },
  { id: "pubmed", name: "PubMed 文献", name_en: "PubMed", icon: "📚", enabled: true, timeout: 60, order: 5, estimated_duration: 60 },
  { id: "cde_reflisted", name: "CDE 参比制剂目录", name_en: "CDE Ref Listed", icon: "🇨🇳", enabled: true, timeout: 35, order: 6, estimated_duration: 35 },
  { id: "cde_trials", name: "CDE 临床试验", name_en: "CDE Trials", icon: "🔬", enabled: true, timeout: 30, order: 7, estimated_duration: 30 },
  { id: "nmpa", name: "NMPA 国家药监局", name_en: "NMPA", icon: "🏥", enabled: false, timeout: 25, order: 8, estimated_duration: 25 },
  { id: "fda_review", name: "FDA 审评报告", name_en: "FDA Review", icon: "📝", enabled: true, timeout: 90, order: 9, estimated_duration: 90 },
  { id: "cde_yzxpj", name: "CDE 一致性评价", name_en: "CDE YZXPJ", icon: "✅", enabled: true, timeout: 45, order: 10, estimated_duration: 45 },
  { id: "dailymed", name: "DailyMed 药品信息", name_en: "DailyMed", icon: "💊", enabled: true, timeout: 45, order: 11, estimated_duration: 45 },
  { id: "pubchem", name: "PubChem 分子结构", name_en: "PubChem Molecular Structure", icon: "Mol", enabled: true, timeout: 30, order: 12, estimated_duration: 30 },
];

export async function checkSourcesHealth(): Promise<{
  checked_at: number;
  sources: SourceHealth[];
  summary: {
    total: number;
    healthy: number;
    error: number;
    not_configured: number;
  };
}> {
  const res = await fetch(`${API_BASE}/api/sources/check`);
  if (!res.ok) throw new Error(`健康检查失败: ${res.status}`);
  return res.json();
}

export async function getSystemStats(): Promise<SystemStats> {
  const res = await fetch(`${API_BASE}/api/stats`);
  if (!res.ok) throw new Error(`获取统计信息失败: ${res.status}`);
  return res.json();
}

export async function getSystemConfig(): Promise<SystemConfig> {
  const res = await fetch(`${API_BASE}/api/config`);
  if (!res.ok) throw new Error(`获取配置失败: ${res.status}`);
  return res.json();
}

export interface TaskInfo {
  task_id: string;
  drug_name: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export async function fetchRecentTasks(limit: number = 20): Promise<TaskInfo[]> {
  const res = await fetch(`${API_BASE}/api/tasks/recent?limit=${limit}`);
  if (!res.ok) throw new Error(`获取任务列表失败: ${res.status}`);
  return res.json();
}

export interface GenerateReportRequest {
  task_id: string;
  format?: "word" | "pdf" | "markdown";
  include_sources?: boolean;
  include_charts?: boolean;
}

export interface GenerateReportResponse {
  report_id: string;
  status: "completed" | "error";
  format?: string;
  download_url?: string;
  file_path?: string;
  error?: string;
}

export interface ReportStatusResponse {
  report_id: string;
  status: string;
  message: string;
}

export async function generateReport(req: GenerateReportRequest): Promise<GenerateReportResponse> {
  const res = await fetch(`${API_BASE}/api/reports/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });
  if (!res.ok) throw new Error(`生成报告失败: ${res.status}`);
  return res.json();
}

export async function pollReportStatus(reportId: string): Promise<ReportStatusResponse> {
  const res = await fetch(`${API_BASE}/api/reports/${reportId}`);
  if (!res.ok) throw new Error(`查询报告状态失败: ${res.status}`);
  return res.json();
}
